/**
 * Static asset URL helpers. Photos live in the repo-root `assets/` dir, served by Vite under
 * `/assets/...` via a symlink at `front-end/public/assets`.
 *
 * Two slug-based getters take care of mapping back-end content slugs (which we don't control
 * from the FE) to the renamed image files. Returns `null` when no image exists for a slug —
 * callers should render a graceful fallback.
 */

export const HOME_HERO = '/assets/home-hero.jpg';
export const LOGO = '/assets/logo.png';
export const FIGHTERS_GROUP = '/assets/fighters/group.jpg';

/**
 * TEMPORARY: hero-photo bake-off candidates kept until the final asset is
 * selected and set back into HOME_HERO.
 * `objectPosition` is per-photo so faces/action stay in frame after the wide
 * hero crop.
 */
export const HERO_CANDIDATES: ReadonlyArray<{
  src: string;
  label: string;
  objectPosition: string;
}> = [
  // Wide group lineup, color. Heads sit in the upper third — anchor toward top.
  { src: '/assets/heroes/hero-1.jpg', label: 'group class (color)', objectPosition: '50% 35%' },
  // B&W: George demonstrating to a student. Both standing, heads near top.
  { src: '/assets/heroes/hero-2.jpg', label: 'George teaching (B&W)', objectPosition: '50% 30%' },
  // Fight team posed in front of the ring. Faces in upper-mid.
  { src: '/assets/heroes/hero-3.jpg', label: 'fight team (color)', objectPosition: '50% 35%' },
  // Crew in the ring, wider landscape. Default center works.
  {
    src: '/assets/heroes/hero-4.jpg',
    label: 'crew in the ring (color)',
    objectPosition: '50% 50%',
  },
  // Class group photo, square. Crowd packed in the lower half — anchor low.
  {
    src: '/assets/heroes/hero-5.jpg',
    label: 'class group photo (color)',
    objectPosition: '50% 65%',
  },
  // Original hero: portrait warmup circle. Subjects sit in the upper-middle.
  { src: '/assets/home-hero.jpg', label: 'warmup circle (original)', objectPosition: '50% 40%' },
];

export const CLASS_GALLERY = [
  '/assets/classes/01-partner-drill.jpg',
  '/assets/classes/02-muay-thai-pair.jpg',
  '/assets/classes/03-shadow-flags.jpg',
  '/assets/classes/04-group-stance.jpg',
  '/assets/classes/05-texas-flag.jpg',
  '/assets/classes/06-ring-mexican.jpg',
  '/assets/classes/07-womens-class.jpg',
  '/assets/classes/08-stance-pair.jpg',
] as const;

interface ProgramImage {
  src: string;
  /** CSS `object-position` override; defaults to "50% 50%" (center). */
  objectPosition?: string;
  /**
   * CSS `transform` applied inside the overflow-hidden tile. Use this when
   * `object-position` doesn't visibly shift (image aspect ratio leaves too
   * little slack inside the container) — `scale(>1) translateY(±%)` zooms in
   * to create slack and then moves the image within it.
   */
  transform?: string;
}

const PROGRAM_IMAGES: Record<string, ProgramImage> = {
  boxing: {
    src: '/assets/instagram/B1Fl9aAHr0S_01.jpg',
    objectPosition: '71% 36%',
  },
  'muay-thai': {
    src: '/assets/instagram/Ch9qaeSOSXx_01.jpg',
    objectPosition: '50% 13%',
  },
  mma: {
    src: '/assets/instagram/B4ihm1CHy2d_01.jpg',
    objectPosition: '50% 35%',
  },
  bjj: {
    src: '/assets/instagram/CNF_KsWsPEM_01.jpg',
    objectPosition: '31% 78%',
  },
  kids: { src: '/assets/programs/kids.jpg' },
};

interface InstructorImage {
  src: string;
  /** CSS `object-position` override; defaults to "50% 0%" (object-top). */
  objectPosition?: string;
  /** Optional CSS `transform` (applied inside the overflow-hidden card). */
  transform?: string;
}

/**
 * TEMPORARY: George portrait bake-off. The home page and instructors page read
 * the active candidate from localStorage so the toggle button syncs across
 * routes. Drop this array (and the toggle) once the owner picks one.
 */
export const GEORGE_PORTRAIT_CANDIDATES: ReadonlyArray<{
  label: string;
  image: InstructorImage;
}> = [
  {
    label: 'with a student (laughing, B&W)',
    image: {
      src: '/assets/instructors/george-prevalsky-1.jpg',
      objectPosition: '25% 50%',
      transform: 'scale(1.4)',
    },
  },
  {
    label: 'on the ring (smiling, color)',
    image: {
      src: '/assets/instructors/george-prevalsky-2.jpg',
      // Solo square photo. Anchor toward top to keep his face centered in the
      // 3:4 portrait card; mild scale so cross-legged crop sits natural.
      objectPosition: '50% 35%',
      transform: 'scale(1.05)',
    },
  },
];

const GEORGE_KEY = 'preview:george-portrait-index';

function readGeorgeIndex(): number {
  if (typeof window === 'undefined') return 0;
  const stored = window.localStorage.getItem(GEORGE_KEY);
  if (stored === null) return 0;
  const n = Number(stored);
  return Number.isFinite(n) && n >= 0 && n < GEORGE_PORTRAIT_CANDIDATES.length ? n : 0;
}

export function setGeorgePortraitIndex(i: number): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(GEORGE_KEY, String(i));
}

const INSTRUCTOR_IMAGES: Record<string, InstructorImage | (() => InstructorImage)> = {
  // All portraits are vertical (~3:5 ratio). Rendered in aspect-[3/4] containers so the
  // head crop is minimal — no per-image objectPosition overrides needed.
  'george-prevalsky': () => GEORGE_PORTRAIT_CANDIDATES[readGeorgeIndex()].image,
  // Confirmed by owner 2026-04-29: Kevin = muay-thai coach photo (coach-2);
  // Victor's role is strictly Boxing.
  'kevin-kildea': { src: '/assets/instructors/coach-2.jpg' },
  'victor-ling': { src: '/assets/instructors/coach-3.jpg' },
  // Yousef's portrait isn't among the scraped set — fallback initials are used.
  'yousef-alirezaei': { src: '/assets/instructors/coach-bjj.jpg' },
};

export function programImage(slug: string): ProgramImage | null {
  return PROGRAM_IMAGES[slug] ?? null;
}

export function instructorImage(slug: string): InstructorImage | null {
  const entry = INSTRUCTOR_IMAGES[slug];
  if (!entry) return null;
  return typeof entry === 'function' ? entry() : entry;
}
